/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import {
	ResumeCitizenAttendancesIndicatorB2Data,
	getResumeCitizensAttendancesIndicatorB2,
} from "@/services/esus/indicator/esb/b2/getResumeCitizensAttendancesIndicatorB2";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorB2,
	ResumeIndicatorB2Data,
} from "@/services/esus/indicator/esb/b2/getResumeIndicatorB2";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorB2ByHealthTeam,
	ResumeIndicatorB2ByHealthTeamData,
} from "@/services/esus/indicator/esb/b2/getResumeIndicatorB2ByHealthTeam";

import { IndicatorEsbB2Resume } from "./IndicatorEsbB2Resume";
import { IndicatorEsbB2Attendances } from "./IndicatorEsbB2Attendances";
import { IndicatorEsbB2HealthTeams } from "./IndicatorEsbB2HealthTeams";

type IndicatorEsbB2Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsbB2({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsbB2Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorB2, setResumeIndicatorB2] = useState<ResumeIndicatorB2Data>(
		new ResumeIndicatorB2Data()
	);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchAttendances, setSearchAttendances] = useState("");
	const debouncedSearchAttendances = useDebounce(searchAttendances, 2000);
	const [isEvaluatedAttendances, setIsEvaluatedAttendances] = useState<boolean | undefined>(true);
	const [paginatedAttendances, setPaginatedAttendances] = useState(
		new Paginated<ResumeCitizenAttendancesIndicatorB2Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorB2ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorB2(),
			handleGetResumeIndicatorB2ByHealthTeam(),
			handleGetResumeCitizensAttendancesIndicatorB2(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorB2() {
		setResumeIndicatorB2(
			await getResumeIndicatorB2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorB2ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorB2ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorB2ByHealthTeam() {
		return (
			await getResumeIndicatorB2ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensAttendancesIndicatorB2(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedAttendances(
			await getResumeCitizensAttendancesIndicatorB2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchAttendances,
				isEvaluated: isEvaluatedAttendances,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllResumeCitizensAttendancesIndicatorB2() {
		return (
			await getResumeCitizensAttendancesIndicatorB2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchAttendances,
				isEvaluated: isEvaluatedAttendances,
				pageNumber: 1,
				pageSize: paginatedAttendances.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeCitizensAttendancesIndicatorB2(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetResumeCitizensAttendancesIndicatorB2(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendances, isEvaluatedAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorB2ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorB2ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("B2 - Tratamentos Odontológicos Concluídos");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsbB2Resume resume={resumeIndicatorB2} />
			<IndicatorEsbB2HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorB2ByHealthTeam={
					handleGetAllResumeIndicatorB2ByHealthTeam
				}
			/>
			<IndicatorEsbB2Attendances
				paginatedAttendances={paginatedAttendances}
				isLoading={isLoadingAttendances}
				search={searchAttendances}
				setSearch={setSearchAttendances}
				isEvaluated={isEvaluatedAttendances}
				setIsEvaluated={setIsEvaluatedAttendances}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllResumeCitizensAttendancesIndicatorB2={
					handleGetAllResumeCitizensAttendancesIndicatorB2
				}
			/>
		</>
	);
}
